/** New-client onboarding: the board of clients being onboarded, each client's onboarding page, and the intake forms. */
import { useMutation, useQuery } from "@tanstack/react-query";
import { Building2, Check, Circle, Clock, Columns3, Rows3, User } from "lucide-react";
import { useState, type ReactNode } from "react";
import { Link } from "wouter";
import { ask } from "@/components/dialogs";
import { FormLibrary, type FormTemplate } from "@/components/organizer-builder";
import { Empty, ListSearch, Loading, PageHeader, Pager, Section, Segmented, ShowMore, usePaged } from "@/components/page";
import { Responses, SignaturesPanel } from "@/components/tax-panels";
import { Button } from "@/components/ui/button";
import { api, errorMessage } from "@/lib/api";
import { formatDate, formatDateTime } from "@/lib/format";
import { useMemberNames } from "@/lib/members";
import { hasPermission, type Me } from "@/lib/session";
import { cn } from "@/lib/utils";
import { PortalAccess, Services, type ClientDetail } from "@/pages/staff/client-detail";
import { TableScroll } from "@/components/table-scroll";

export type Onboarding = {
  clientId: string;
  displayName: string;
  kind: "individual" | "business";
  status: string;
  email: string | null;
  assignedUserId: string | null;
  createdAt: string;
  services: number;
  portal: { invited: number; active: number };
  intake: { id: string; status: "open" | "submitted"; answered: number; total: number } | null;
  engagementLetter: "none" | "sent" | "signed";
  documents: { requested: number; received: number };
  done: number;
};

type StepState = "done" | "waiting" | "todo";
type Step = { label: string; state: StepState; detail: string };

/** The five onboarding steps for one client, in order. */
export function onboardingSteps(o: Onboarding): Step[] {
  return [
    { label: "Services selected", state: o.services > 0 ? "done" : "todo", detail: o.services > 0 ? `${o.services} service${o.services > 1 ? "s" : ""}` : "No services selected" },
    {
      label: "Client portal access",
      state: o.portal.active > 0 ? "done" : o.portal.invited > 0 ? "waiting" : "todo",
      detail: o.portal.active > 0 ? "Invitation accepted" : o.portal.invited > 0 ? "Invitation sent" : "Not invited",
    },
    {
      label: "Intake form",
      state: o.intake?.status === "submitted" ? "done" : o.intake ? "waiting" : "todo",
      detail: o.intake?.status === "submitted" ? "Submitted" : o.intake ? `${o.intake.answered} of ${o.intake.total} answered` : "Not sent",
    },
    {
      label: "Engagement letter",
      state: o.engagementLetter === "signed" ? "done" : o.engagementLetter === "sent" ? "waiting" : "todo",
      detail: o.engagementLetter === "signed" ? "Signed" : o.engagementLetter === "sent" ? "Sent for signature" : "Not sent",
    },
    {
      label: "Onboarding documents",
      state: o.intake?.status !== "submitted" ? "todo" : o.documents.received === o.documents.requested ? "done" : "waiting",
      detail: o.intake?.status !== "submitted" ? "Requested after the intake form" : o.documents.requested ? `${o.documents.received} of ${o.documents.requested} received` : "None required",
    },
  ];
}

const STATE_STYLE: Record<StepState, string> = {
  done: "bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-200 dark:border-emerald-800",
  waiting: "bg-amber-50 text-amber-800 border-amber-300 dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-800",
  todo: "bg-background text-muted-foreground border-border",
};

function StepIcon({ state }: { state: StepState }) {
  if (state === "done") return <Check className="h-3.5 w-3.5" />;
  if (state === "waiting") return <Clock className="h-3.5 w-3.5" />;
  return <Circle className="h-3.5 w-3.5" />;
}

function Progress({ done }: { done: number }) {
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-24 overflow-hidden rounded-full bg-secondary">
        <div className="h-full rounded-full bg-primary" style={{ width: `${(done / 5) * 100}%` }} />
      </div>
      <span className="whitespace-nowrap text-xs tabular text-muted-foreground">{done} of 5</span>
    </div>
  );
}

/** Board columns: each prospect sits under the first step not yet finished. */
const COLUMNS = ["Services", "Portal access", "Intake form", "Engagement letter", "Documents", "Ready to activate"];
const columnOf = (o: Onboarding) => {
  const i = onboardingSteps(o).findIndex((st) => st.state !== "done");
  return i < 0 ? COLUMNS.length - 1 : i;
};

function ClientName({ o }: { o: Onboarding }) {
  return (
    <Link href={`/clients/onboarding/${o.clientId}`} className="flex items-start gap-2 font-medium leading-snug hover:underline">
      {o.kind === "business" ? <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" /> : <User className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />}
      <span className="line-clamp-2 break-words" title={o.displayName}>
        {o.displayName}
      </span>
    </Link>
  );
}

/** Clients › Onboarding: everyone still being onboarded, as a board by step or as a table. */
export function OnboardingPage({ me }: { me: Me }) {
  const [search, setSearch] = useState("");
  const term = search.trim();
  const { data: res, isLoading } = useQuery<{ items: Onboarding[]; total: number }>({ queryKey: [term ? `/api/onboarding?${new URLSearchParams({ q: term })}` : "/api/onboarding"], placeholderData: (prev) => prev });
  const data = res?.items;
  const page = usePaged(data ?? [], { resetKey: term });
  const names = useMemberNames();
  const [layout, setLayout] = useState<"board" | "table">(() => {
    try {
      return localStorage.getItem("onboarding-layout") === "table" ? "table" : "board";
    } catch {
      return "board";
    }
  });
  const choose = (l: "board" | "table") => {
    setLayout(l);
    try {
      localStorage.setItem("onboarding-layout", l);
    } catch {
      /* not remembered */
    }
  };
  const manager = (o: Onboarding) => (o.assignedUserId ? (names.get(o.assignedUserId) ?? "—") : "Unassigned");
  return (
    <>
      <PageHeader
        crumbs={[{ label: "Clients", href: "/clients" }]}
        title="Onboarding"
        description="New clients are added as prospects and stay here until onboarding is complete. Open a client to see and run every step."
        actions={
          <Segmented
            label="Layout"
            value={layout}
            onChange={choose}
            options={[
              { value: "board", label: <><Columns3 className="h-3.5 w-3.5" aria-hidden /> Board</> },
              { value: "table", label: <><Rows3 className="h-3.5 w-3.5" aria-hidden /> Table</> },
            ]}
          />
        }
      />
      {(term || (res?.total ?? 0) > 10) && (
        <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2">
          <ListSearch value={search} onChange={setSearch} placeholder="Search name or email" />
          <p className="text-sm text-muted-foreground">
            {res && res.items.length < res.total ? `Showing the newest ${res.items.length.toLocaleString("en-US")} of ${res.total.toLocaleString("en-US")}. Search to find the others.` : res ? `${res.total.toLocaleString("en-US")} client${res.total === 1 ? "" : "s"}` : ""}
          </p>
        </div>
      )}
      {isLoading ? (
        <Loading />
      ) : !data?.length ? (
        <Empty>{term ? `No clients in onboarding match "${term}".` : "No clients are being onboarded. Use New client on the Clients page to start onboarding a client."}</Empty>
      ) : layout === "board" ? (
        // One row, left to right in the order of the steps; it scrolls sideways on smaller screens.
        <div className="flex gap-2 overflow-x-auto pb-2">
          {COLUMNS.map((col, ci) => {
            const cards = data.filter((o) => columnOf(o) === ci);
            return (
              <section key={col} aria-label={col} className="flex min-h-[16rem] min-w-[180px] flex-1 flex-col rounded-lg border border-card-border bg-secondary/40">
                <header className="flex items-center justify-between border-b border-border px-3 py-2">
                  <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{col}</h2>
                  <span className="rounded-full bg-card px-2 text-xs tabular">{cards.length}</span>
                </header>
                <div role="list" className="flex-1 space-y-2 p-2">
                  {/* A long column shows its first 10; "Show all" opens the rest. */}
                  <ShowMore
                    items={cards}
                    initial={10}
                    noun="clients"
                    render={(o) => (
                      <div role="listitem" key={o.clientId} className="rounded-md border border-card-border bg-card p-3 shadow-sm">
                        <ClientName o={o} />
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          {manager(o)} · added {formatDate(o.createdAt)}
                        </p>
                        <div className="mt-2 flex gap-1" aria-label={`${o.done} of 5 steps done`}>
                          {onboardingSteps(o).map((st) => (
                            <span key={st.label} title={`${st.label}: ${st.detail}`} className={cn("h-1.5 flex-1 rounded-full", st.state === "done" ? "bg-emerald-500" : st.state === "waiting" ? "bg-amber-400" : "bg-border")} />
                          ))}
                        </div>
                        <p className="mt-1.5 text-xs text-muted-foreground">{ci < 5 ? onboardingSteps(o)[ci]!.detail : "Every step is done"}</p>
                      </div>
                    )}
                  />
                </div>
              </section>
            );
          })}
        </div>
      ) : (
        <Section title="Clients in onboarding">
          <TableScroll label="Clients in onboarding table">
            <table className="w-full min-w-[760px] text-sm">
              <thead className="text-left text-xs text-muted-foreground">
                <tr className="border-b border-border">
                  <th className="py-2 pr-4 font-medium">Client</th>
                  <th className="py-2 pr-4 font-medium">Progress</th>
                  <th className="py-2 pr-4 font-medium">Steps</th>
                  <th className="py-2 font-medium">Client manager</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {page.items.map((o) => (
                  <tr key={o.clientId} className="align-top">
                    <td className="py-3 pr-4">
                      <ClientName o={o} />
                      <p className="mt-0.5 text-xs text-muted-foreground">Added {formatDate(o.createdAt)}</p>
                    </td>
                    <td className="py-3 pr-4">
                      <Progress done={o.done} />
                    </td>
                    <td className="py-3 pr-4">
                      <div className="flex flex-wrap gap-1.5">
                        {onboardingSteps(o).map((st) => (
                          <span key={st.label} title={st.detail} className={cn("inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium", STATE_STYLE[st.state])}>
                            <StepIcon state={st.state} /> {st.label}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="py-3 text-muted-foreground">{manager(o)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
          <Pager paged={page} noun="clients" className="mt-2" />
        </Section>
      )}
    </>
  );
}

type OnboardingDetail = Onboarding & {
  letters: Array<{ id: string; title: string; status: string; createdAt: string; completedAt: string | null }>;
  documentList: Array<{ id: string; title: string; type: string; status: "open" | "submitted" | "accepted"; respondedAt: string | null }>;
  intakeHistory: Array<{ id: string; title: string; status: "open" | "submitted" | "cancelled"; createdAt: string; submittedAt: string | null }>;
  events: Array<{ at: string; label: string }>;
};

const REQUEST_STATUS: Record<string, [string, string]> = {
  open: ["Waiting for the client", "bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200"],
  submitted: ["Received — to review", "bg-sky-50 text-sky-800 dark:bg-sky-950/40 dark:text-sky-200"],
  accepted: ["Accepted", "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200"],
};

function Step({ n, title, state, detail, children }: { n: number; title: string; state: StepState; detail: string; children: ReactNode }) {
  return (
    <li className="relative sm:pl-12">
      <span
        aria-hidden
        className={cn(
          "absolute left-0 top-4 hidden h-8 w-8 items-center justify-center rounded-full border text-sm font-semibold sm:flex",
          state === "done" ? "border-emerald-500 bg-emerald-500 text-white" : state === "waiting" ? "border-amber-400 bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200" : "border-border bg-card text-muted-foreground",
        )}
      >
        {state === "done" ? <Check className="h-4 w-4" /> : n}
      </span>
      <section className="rounded-lg border border-card-border bg-card">
        <header className="flex flex-wrap items-baseline justify-between gap-2 border-b border-border px-5 py-3">
          <h2 className="text-[15px] font-semibold">
            {n}. {title}
          </h2>
          <span className={cn("rounded-full border px-2 py-0.5 text-[11px] font-medium", STATE_STYLE[state])}>{detail}</span>
        </header>
        <div className="px-5 py-4">{children}</div>
      </section>
    </li>
  );
}

/** One prospect's whole onboarding: every step with what happened and what to do next, and the history. */
export function OnboardingDetailPage({ id, me }: { id: string; me: Me }) {
  const { data, isLoading, error } = useQuery<OnboardingDetail>({ queryKey: ["/api/clients/", id, "/onboarding/detail"] });
  const { data: client } = useQuery<ClientDetail>({ queryKey: ["/api/clients/", id] });
  const { data: templates } = useQuery<FormTemplate[]>({ queryKey: ["/api/organizer-templates"] });
  const [templateId, setTemplateId] = useState("");
  const send = useMutation({ meta: { inlineError: true }, mutationFn: (tpl: string) => api.send("POST", "/api/organizers", { clientId: id, templateId: tpl }) });
  const reopen = useMutation({ meta: { inlineError: true }, mutationFn: (oid: string) => api.send("POST", `/api/organizers/${oid}/reopen`) });
  const withdraw = useMutation({ meta: { inlineError: true }, mutationFn: (oid: string) => api.send("POST", `/api/organizers/${oid}/cancel`) });
  const complete = useMutation({ meta: { inlineError: true }, mutationFn: () => api.send("PATCH", `/api/clients/${id}`, { status: "active" }) });
  if (isLoading || !client) return <Loading />;
  if (error || !data) return <Empty>{errorMessage(error)}</Empty>;
  const steps = onboardingSteps(data);
  const canEdit = hasPermission(me, "clients.write");
  const canSend = hasPermission(me, "requests.write");
  const intakes = (templates ?? []).filter((t) => t.kind === "intake" && t.isActive);
  const suggested = intakes.find((t) => t.name.toLowerCase().includes(data.kind)) ?? intakes[0];
  const current = data.intakeHistory.find((o) => o.status !== "cancelled");
  const err = send.error ?? reopen.error ?? withdraw.error ?? complete.error;
  return (
    <>
      <PageHeader
        crumbs={[
          { label: "Clients", href: "/clients" },
          { label: "Onboarding", href: "/clients/onboarding" },
        ]}
        title={
          <span className="flex items-center gap-2.5">
            {data.kind === "business" ? <Building2 className="h-5 w-5 text-muted-foreground" /> : <User className="h-5 w-5 text-muted-foreground" />}
            {data.displayName}
          </span>
        }
        description={data.status === "prospect" ? <Progress done={data.done} /> : "Onboarding is complete; this client is active."}
        actions={
          <>
            <Button variant="outline" asChild>
              <Link href={`/clients/${id}`}>Open client record</Link>
            </Button>
            {canEdit && data.status === "prospect" && (
              <Button
                variant={data.done === 5 ? "default" : "outline"}
                disabled={complete.isPending}
                onClick={async () => {
                  if (data.done === 5 || (await ask({ title: "Complete onboarding now?", description: `${5 - data.done} step${5 - data.done > 1 ? "s are" : " is"} not finished. The client becomes active and leaves the onboarding list.`, confirmLabel: "Complete onboarding" })))
                    complete.mutate();
                }}
              >
                Complete onboarding
              </Button>
            )}
          </>
        }
      />
      {err && <p className="mb-4 text-sm text-destructive">{errorMessage(err)}</p>}
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
        <ol className="space-y-4">
          <Step n={1} title="Services" state={steps[0]!.state} detail={steps[0]!.detail}>
            <Services id={id} data={client} canEdit={canEdit} />
          </Step>
          <Step n={2} title="Client portal access" state={steps[1]!.state} detail={steps[1]!.detail}>
            <PortalAccess id={id} data={client} canEdit={canEdit} />
          </Step>
          <Step n={3} title="Intake form" state={steps[2]!.state} detail={steps[2]!.detail}>
            {!current ? (
              intakes.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No intake form is active. Create one under <Link href="/clients/intake-forms" className="font-medium text-primary hover:underline">Clients › Intake Forms</Link>.
                </p>
              ) : canSend ? (
                <div className="flex flex-wrap items-center gap-2">
                  <select aria-label="Intake form" className="h-9 rounded-md border border-input bg-background px-2 text-sm" value={templateId || suggested?.id} onChange={(e) => setTemplateId(e.target.value)}>
                    {intakes.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                  <Button size="sm" disabled={send.isPending} onClick={() => send.mutate(templateId || suggested!.id)}>
                    Send intake form
                  </Button>
                  <span className="text-xs text-muted-foreground">It is emailed to everyone invited to the portal, even before they sign in.</span>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Not sent yet.</p>
              )
            ) : (
              <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="font-medium">{current.title}</span>
                  <span className="text-muted-foreground">
                    · sent {formatDate(current.createdAt)}
                    {current.submittedAt && ` · submitted ${formatDate(current.submittedAt)}`}
                  </span>
                  <span className="ml-auto flex gap-2">
                    {canSend && current.status === "submitted" && (
                      <Button size="sm" variant="outline" onClick={async () => (await ask({ title: "Send the form back to the client?", description: "They can change their answers and submit again. Their current answers are kept.", confirmLabel: "Send back" })) && reopen.mutate(current.id)}>
                        Send back to client
                      </Button>
                    )}
                    {canSend && current.status === "open" && (
                      <Button size="sm" variant="ghost" onClick={async () => (await ask({ title: "Withdraw this intake form?", description: "The client will no longer see it. You can then send a different form.", confirmLabel: "Withdraw form", destructive: true })) && withdraw.mutate(current.id)}>
                        Withdraw
                      </Button>
                    )}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">The answers are the client's own and can't be changed by staff. To correct one, send the form back to the client.</p>
                <details open={current.status === "submitted"} className="rounded-md border border-border">
                  <summary className="cursor-pointer px-3 py-2 text-sm font-medium">The client's answers</summary>
                  <div className="border-t border-border p-3">
                    <Responses id={current.id} />
                  </div>
                </details>
              </div>
            )}
          </Step>
          <Step n={4} title="Engagement letter" state={steps[3]!.state} detail={steps[3]!.detail}>
            <SignaturesPanel clientId={id} me={me} portalUsers={client.portalUsers} />
          </Step>
          <Step n={5} title="Documents the intake asked for" state={steps[4]!.state} detail={steps[4]!.detail}>
            {!data.documentList.length ? (
              <p className="text-sm text-muted-foreground">{current?.status === "submitted" ? "The client's answers didn't call for any documents." : "The documents appear here once the client submits the intake form."}</p>
            ) : (
              <>
                <ul className="divide-y divide-border rounded-md border border-border">
                  {data.documentList.map((doc) => (
                    <li key={doc.id} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm">
                      <span className="flex-1">{doc.title}</span>
                      {doc.respondedAt && <span className="text-xs text-muted-foreground">{formatDate(doc.respondedAt)}</span>}
                      <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-medium", REQUEST_STATUS[doc.status]?.[1])}>{REQUEST_STATUS[doc.status]?.[0] ?? doc.status}</span>
                    </li>
                  ))}
                </ul>
                <Link href={`/clients/${id}?tab=requests`} className="mt-2 inline-block text-sm font-medium text-primary hover:underline">
                  Review them in Requests
                </Link>
              </>
            )}
          </Step>
        </ol>
        <aside aria-label="Onboarding history">
          <Section title="History">
            <ol className="space-y-3">
              {data.events.map((e, i) => (
                <li key={i} className="flex gap-3 text-sm">
                  <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary/60" aria-hidden />
                  <span className="flex-1">
                    {e.label}
                    <span className="block text-xs text-muted-foreground">{formatDateTime(e.at)}</span>
                  </span>
                </li>
              ))}
            </ol>
          </Section>
        </aside>
      </div>
    </>
  );
}

/** Clients › Intake Forms: the questionnaires new clients fill in, with a preview of what they see. */
export function IntakeFormsPage({ me }: { me: Me }) {
  return (
    <>
      <PageHeader crumbs={[{ label: "Clients", href: "/clients" }]} title="Intake Forms" description="The questionnaires new clients fill in during onboarding. Preview a form exactly as the client sees it, or edit it." />
      <FormLibrary kind="intake" me={me} />
    </>
  );
}

/** The onboarding checklist on a client's Overview, with the next actions. */
export function OnboardingTracker({ clientId, me, onOpenTab }: { clientId: string; me: Me; onOpenTab: (tab: string) => void }) {
  const { data } = useQuery<Onboarding>({ queryKey: ["/api/clients/", clientId, "/onboarding"] });
  const { data: templates } = useQuery<FormTemplate[]>({ queryKey: ["/api/organizer-templates"] });
  const [templateId, setTemplateId] = useState("");
  const send = useMutation({ meta: { inlineError: true }, mutationFn: (id: string) => api.send("POST", "/api/organizers", { clientId, templateId: id }) });
  const complete = useMutation({ meta: { inlineError: true }, mutationFn: () => api.send("PATCH", `/api/clients/${clientId}`, { status: "active" }) });
  if (!data || data.status !== "prospect") return null;
  const steps = onboardingSteps(data);
  const intakes = (templates ?? []).filter((t) => t.kind === "intake" && t.isActive);
  const suggested = intakes.find((t) => t.name.toLowerCase().includes(data.kind)) ?? intakes[0];
  const canEdit = hasPermission(me, "clients.write");

  return (
    <Section title="Onboarding" description="This client is a prospect until onboarding is complete." className="lg:col-span-2" action={<Progress done={data.done} />}>
      <ol className="grid gap-3 md:grid-cols-5">
        {steps.map((s, i) => (
          <li key={s.label} className={cn("rounded-md border px-3 py-2.5", STATE_STYLE[s.state])}>
            <p className="flex items-center gap-1.5 text-xs font-semibold">
              <StepIcon state={s.state} /> {i + 1}. {s.label}
            </p>
            <p className="mt-1 text-xs">{s.detail}</p>
          </li>
        ))}
      </ol>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {!data.intake && hasPermission(me, "requests.write") && intakes.length > 0 && (
          <>
            <select aria-label="Intake form" className="h-9 rounded-md border border-input bg-background px-2 text-sm" value={templateId || suggested?.id} onChange={(e) => setTemplateId(e.target.value)}>
              {intakes.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
            <Button size="sm" disabled={send.isPending} onClick={() => send.mutate(templateId || suggested!.id)}>
              Send intake form
            </Button>
          </>
        )}
        {data.portal.invited === 0 && (
          <Button size="sm" variant="outline" onClick={() => document.getElementById("portal-access")?.scrollIntoView({ behavior: "smooth", block: "center" })}>
            Invite to the client portal
          </Button>
        )}
        {data.engagementLetter === "none" && (
          <Button size="sm" variant="outline" onClick={() => onOpenTab("documents")}>
            Send engagement letter
          </Button>
        )}
        <Button size="sm" variant="ghost" asChild>
          <Link href={`/clients/onboarding/${clientId}`}>Open onboarding</Link>
        </Button>
        {canEdit && (
          <Button
            size="sm"
            variant={data.done === 5 ? "default" : "ghost"}
            className="ml-auto"
            disabled={complete.isPending}
            onClick={async () => {
              if (data.done === 5 || (await ask({ title: "Complete onboarding now?", description: `${5 - data.done} step${5 - data.done > 1 ? "s are" : " is"} not finished. The client becomes active and leaves the onboarding list.`, confirmLabel: "Complete onboarding" })))
                complete.mutate();
            }}
          >
            Complete onboarding
          </Button>
        )}
      </div>
      {(send.error || complete.error) && <p className="mt-2 text-sm text-destructive">{errorMessage(send.error ?? complete.error)}</p>}
    </Section>
  );
}
